import { Table } from '@dev-spendesk/grapes';
import React from 'react';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import { QuerySuspense } from 'common/components/QuerySuspense';
import { useTranslation } from 'common/hooks/useTranslation';
import {
  filterIntegrationVatAccounts,
  type IntegrationVatAccount,
  type TaxAccount,
} from 'modules/bookkeep/settings/accounting';
import { type QueryState } from 'src/core/api/queryState';

import { TaxAccountErrorWarning } from '../../TaxAccountErrorWarning';
import { TaxAccountSkeletonTable } from '../../TaxAccountSkeletonTable';
import { TableFooter } from '../common/CommonFooter';

interface Props {
  taxAccountQueryState: QueryState<TaxAccount[]>;
}

export const NetsuiteTaxAccountPullTable = ({
  taxAccountQueryState,
}: Props) => {
  const { t } = useTranslation('global');
  return (
    <QuerySuspense
      queryState={taxAccountQueryState}
      loading={<TaxAccountSkeletonTable />}
      fallback={() => <TaxAccountErrorWarning />}
    >
      {(taxAccounts) => {
        const vatAccounts = filterIntegrationVatAccounts(taxAccounts);
        return (
          <Table<IntegrationVatAccount>
            rowHeight="compact"
            columns={[
              {
                id: 'vat-account-code',
                header: t('bookkeep.integrations.settings.vatTable.code'),
                width: '33.333%',
                renderCell(row: IntegrationVatAccount) {
                  return row.code;
                },
              },
              {
                id: 'vat-account-rate',
                header: t('bookkeep.integrations.settings.vatTable.rate'),
                width: '33.333%',
                renderCell(row: IntegrationVatAccount) {
                  return `${row.rate} %`;
                },
              },
              {
                id: 'vat-account-description',
                header: t(
                  'bookkeep.integrations.settings.vatTable.description',
                ),
                width: '33.333%',
                renderCell(row: IntegrationVatAccount) {
                  return <EllipsisTooltip text={row.description ?? ''} />;
                },
              },
            ]}
            data={vatAccounts}
            footer={
              taxAccounts.length === 0 ? (
                <TableFooter taxAccountQueryState={taxAccountQueryState} />
              ) : undefined
            }
          />
        );
      }}
    </QuerySuspense>
  );
};
